import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-password", { token, new_password: password });
      setUser(data.user);
      toast.success("Password reset — you're signed in.");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-bg px-6" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex justify-center mb-10">
          <LogoMark size="text-3xl" />
        </Link>

        <div className="bg-white border border-brand-line rounded-lg p-8 shadow-sm">
          <ShieldCheck className="h-8 w-8 text-brand-gold" strokeWidth={1.4} />
          <p className="overline mt-3">Reset password</p>
          <h1 className="font-serif text-3xl text-brand-ink mt-2">Choose a new password</h1>
          <p className="text-sm text-brand-inkLight mt-2">It must be at least 6 characters. You&apos;ll be signed in automatically.</p>

          {!token ? (
            <p className="text-sm text-brand-maroon mt-6">This reset link is missing the token. Request a new one from the <Link to="/forgot-password" className="underline">forgot-password page</Link>.</p>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="reset-form">
              <div>
                <label className="overline">New password</label>
                <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="input-elegant mt-1" data-testid="reset-password" />
              </div>
              <div>
                <label className="overline">Confirm password</label>
                <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="input-elegant mt-1" data-testid="reset-confirm" />
              </div>
              {error && <p className="text-sm text-brand-maroon" data-testid="reset-error">{error}</p>}
              <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="reset-submit">
                {loading ? "Updating…" : "Reset password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
